// Jotai atoms for offline-recording sync state (APP-6190, T020).
//
// Foundation only: these are the in-memory slots the recorder (US1), the sync
// orchestrator (US2), and the status UI (Phase 4) read from and write to. They
// are intentionally not wired to any IPC event yet — that hydration lands with
// the commands/events in later tickets (e.g. APP-6259 for connectivity,
// `useOfflineRecordingEvents` for the queue).

import { atom, getDefaultStore, useAtomValue } from 'jotai';

import { createUserScopedStorageAtom } from '@/lib/jotai-storage/user-scoped';
import {
  isOfflineFailedStatus,
  isOfflinePendingStatus,
  type OfflineConnectivityUiState,
  type OfflineSyncItem,
} from '@/tauri/offline-recording/offline-recording';
import { type OfflineLocalSessionListItem } from '@heidi/session/contracts';

// `OfflineLocalSessionListItem` moved to `@heidi/session/contracts` with the
// combined-sessions core, which unions these rows into the list from inside the
// package. Re-exported so every importer of this path keeps resolving against
// the one declaration.
export type { OfflineLocalSessionListItem } from '@heidi/session/contracts';

// --- Primitive (writable) atoms --------------------------------------------

/**
 * Real backend-reachability state from the Rust connectivity monitor. Defaults
 * to the same `unknown` state Rust uses before a target has been evaluated —
 * the UI stays neutral (no banner) until a confirmed reading, so we never flash
 * a false "you're offline" on first paint. Never assume online before the first
 * read.
 *
 * Do NOT age this atom to judge whether the reading is still trustworthy. Rust
 * publishes `connectivity_changed` only on a state CHANGE (`should_publish` /
 * `publish` in `src-tauri/src/offline_recording/connectivity.rs`), so a write
 * here marks the last TRANSITION, not the last probe: a link healthy for hours
 * looks stale, and a fresh macOS drop looks current. The XHR layer therefore
 * discredits the reading on evidence instead — see
 * `CONNECTIVITY_RECHECK_WINDOW_MS` in `src/utils/http/desktopOfflineNetwork.ts`.
 */
export const offlineConnectivityStateAtom = atom<OfflineConnectivityUiState>('unknown');

/**
 * The durable sync queue — every tracked offline recording. Mirrors the Rust
 * `sync_tracking` table; populated by the offline-recording events that land in
 * US1+.
 */
export const offlineSyncQueueAtom = atom<OfflineSyncItem[]>([]);

/**
 * Local-only offline sessions that exist before the durable sync queue has a
 * row to hydrate. Kept intentionally small: only fields required to make a
 * stable synthetic list row.
 */
export const offlineLocalSessionsAtom = atom<OfflineLocalSessionListItem[]>([]);

/**
 * `localSessionId` of the recording currently capturing, or `null` when idle.
 * Set by the recorder (US1) and by the `offline_recording:capture_started` /
 * `pending_sync` events. This is the single, app-wide source of truth for which
 * capture is live — `useOfflineRecorder` is mounted independently by the record
 * button, the keyboard shortcuts, and the start-prompt overlay, so the active id
 * MUST be shared here rather than held per-hook-instance, or the mount that
 * starts a capture diverges from the one that renders the button or stops it.
 * The recorder derives its `status` ('recording' when non-null, else 'idle')
 * from this atom, so a control click on any mount resolves the live recording.
 */
export const offlineActiveRecordingIdAtom = atom<string | null>(null);

/**
 * Whether the live offline capture is currently paused. Shared for the same
 * reason as {@link offlineActiveRecordingIdAtom}: every `useOfflineRecorder`
 * mount must reflect the same pause state. Only meaningful while a capture is
 * active (a non-null {@link offlineActiveRecordingIdAtom}); consumers mask it to
 * `false` when idle.
 */
export const offlineCapturePausedAtom = atom(false);

/**
 * Whether the user has paused queue-level sync (FR-023). Pause persists across
 * reconnects until the user explicitly resumes.
 */
export const offlineSyncPausedAtom = atom(false);

// UNCARVED DOMAIN KEY (offline-recording, DSK-675): offline recording has no
// workspace package, so there is no /contracts door to declare this in. It sits
// beside the dismissal key it partners, which is already baselined here. Not a
// query cache identity — a browser-storage key, registered in
// persistence-manifest.json.
export const OFFLINE_NOTE_FAILED_SESSION_IDS_STORAGE_KEY =
  'heidi.offlineRecording.noteFailedSessionIds';

/**
 * Sessions whose note generation failed. Kept per-session so dismissing one
 * failure does not suppress a different session, and **persisted** because
 * nothing else remembers it.
 *
 * The durability is not a nicety. `note_failed` is non-fatal, so the engine
 * emits `upload_complete` on the very next statement, which marks the recording
 * `synced` and tears down the only durable record it had: Rust deletes the
 * `sync_tracking` row and the `.enc`, and the renderer sweeps the queue item as
 * soon as the server session lands in the list cache. `drain()` then only ever
 * revisits non-`synced` recordings, so the event cannot be replayed. Without
 * this, a clinician who quits before reading the warning never learns the note
 * is missing.
 *
 * Ids are re-keyed from the local id to the server session id when the recording
 * syncs, while both are still in hand, so a lookup by either resolves.
 *
 * Shares the user-scoped adapter behaviour documented on
 * {@link offlineNoteFailedDismissedSessionIdsAtom} — including that logout
 * sweeps this key — with one difference worth stating: the first-login write
 * window is unreachable here, because `note_failed` only fires from a sync
 * drain, which needs a resolved, online session long after the scope is cached.
 */
export const offlineNoteFailedSessionIdsAtom = createUserScopedStorageAtom<string[]>(
  OFFLINE_NOTE_FAILED_SESSION_IDS_STORAGE_KEY,
  []
);

export const OFFLINE_NOTE_FAILED_DISMISSED_SESSION_IDS_STORAGE_KEY =
  'heidi.offlineRecording.noteFailedDismissedSessionIds';

/**
 * Failed sessions whose warning the current user has dismissed. Persisted so
 * the same session stays quiet across layout remounts and desktop restarts.
 *
 * One known edge, accepted:
 *
 * - **First-login write window.** The scoped adapter no-ops while
 *   `getUserStorageScope()` is `null`, so a dismissal made before
 *   `useCacheUserRefIdForStorage` resolves the id persists nothing, and the
 *   `rehydrateUserScopedAtoms` that follows resets this atom to `[]` — the
 *   warning the user just dismissed reappears. Unavoidable under the rehydrate
 *   contract (the re-read is what makes a cold first login correct at all), and
 *   it needs a note failure inside that first-paint window to be reachable.
 *
 * Logout leaves nothing behind. `clearLogoutStorage()` calls
 * `clearScopedUserPrefs()`, which sweeps every `_<scope>`-suffixed key except the
 * handful in `USER_SCOPED_KEYS_KEPT_ACROSS_LOGOUT` (session-list display
 * preferences). This key is not among them, so the ids are removed at logout
 * rather than merely made unreadable by the next user. The sweep matches on the
 * scope suffix rather than an enumerated list, so a new user-scoped key is
 * covered without having to register anywhere.
 */
export const offlineNoteFailedDismissedSessionIdsAtom = createUserScopedStorageAtom<
  string[]
>(OFFLINE_NOTE_FAILED_DISMISSED_SESSION_IDS_STORAGE_KEY, []);

// UNCARVED DOMAIN KEY (offline-recording, DSK-131): offline-recording has no
// workspace package, so there is no /contracts door to declare this in. Not a
// query cache identity either — a browser-storage key for the user-scoped atom
// below, registered in persistence-manifest.json and never read into a
// queryKey/mutationKey head.
export const OFFLINE_PENDING_SERVER_DELETE_IDS_STORAGE_KEY =
  'heidi.offlineRecording.pendingServerDeleteSessionIds';

/**
 * Server session ids this clinician has deleted while offline and the backend
 * has not accepted yet — the render-time mirror of the Rust `pending_deletes`
 * table.
 *
 * **SQLite is the authority; this is a render cache.** Every divergence
 * resolves in SQLite's favour at the next hydration, and nothing here decides
 * whether a delete is still owed — Rust does, and hands over ids only.
 *
 * Three properties are load-bearing, and each one is a criterion on its own:
 *
 * - **Synchronous.** A quit-and-relaunch while still offline must leave the
 *   session absent from the FIRST painted frame, not visible and then removed.
 *   The persisted session-list snapshot that seeds the list is read
 *   synchronously out of localStorage during render, so the thing that hides
 *   rows from it has to be readable in that same pass. An IPC hydration cannot
 *   be sequenced against a synchronous read, which is why this is an
 *   `atomWithStorage` with `getOnInit` and not a plain atom filled in by an
 *   effect. The IPC read still happens — it reconciles this cache against the
 *   table — but it is never what makes the first frame correct.
 * - **User-scoped.** A queued deletion is private to the clinician who made it.
 *   The scoped adapter namespaces the key by `user_ref_id` and no-ops without
 *   one, so a second clinician on the same machine reads their own (empty)
 *   list rather than the first one's, and logout sweeps the key by suffix.
 * - **Ids only.** No title, patient link, transcript or note text is ever put
 *   in here, so nothing on this path can leak into storage or a log line.
 *
 * Not flag-gated anywhere it is read. A machine that never had offline
 * recording on has an empty mirror, so an ungated read is a no-op there — while
 * gating it would let an umbrella-flag flip resurrect a session whose deletion
 * is still owed.
 */
export const offlinePendingServerDeleteIdsAtom = createUserScopedStorageAtom<string[]>(
  OFFLINE_PENDING_SERVER_DELETE_IDS_STORAGE_KEY,
  []
);

/**
 * The mirror, read outside React.
 *
 * Session-list readers that go straight to the query cache never mount a hook,
 * so they need a synchronous accessor rather than a subscription. Reads the
 * default store deliberately: jotai's `Provider` is not used for this app's
 * atoms, so the default store IS the store every `useAtomValue` here resolves
 * against — a second store would silently give a reader a different mirror from
 * the one the events hook writes.
 */
export const readOfflinePendingServerDeleteIds = (): readonly string[] =>
  getDefaultStore().get(offlinePendingServerDeleteIdsAtom);

/**
 * The mirror, read inside React — the one named subscriber every list surface
 * uses.
 *
 * A hook rather than a bare `useAtomValue(offlinePendingServerDeleteIdsAtom)`
 * at each surface for two reasons. It names the read, so a reader of a list
 * hook can see what it is filtering by without resolving an atom identity. And
 * it gives the several suites that stub jotai's `useAtomValue` wholesale ONE
 * thing to double — a bare atom read would silently resolve to whatever their
 * dispatcher's fallback returns, which is how a filter over `undefined` throws
 * inside an unrelated test.
 *
 * Subscribes, unlike {@link readOfflinePendingServerDeleteIds}: a list on
 * screen when a delete is queued has to re-render without the row.
 */
export const useOfflinePendingServerDeleteIds = (): string[] =>
  useAtomValue(offlinePendingServerDeleteIdsAtom);

/**
 * Add ids to the mirror without dropping any already there.
 *
 * Union rather than replace, in every writer, because the two things that fill
 * this cache — the enqueue event and the durable list read — race by design and
 * neither can speak for the other. A replace would let a list read that started
 * before a delete was queued erase it, which is a deleted session flashing back
 * into the list. Returns `prev` unchanged when there is nothing new, so a
 * repeated event does not re-render every list surface.
 */
export const unionServerSessionIds = (
  prev: string[],
  ids: readonly string[]
): string[] => {
  const additions = ids.filter((id) => id && !prev.includes(id));
  return additions.length === 0 ? prev : [...prev, ...additions];
};

/**
 * Drop exactly one id from the mirror.
 *
 * Id-specific and never a bulk clear: a removal means "this one delete stopped
 * being owed", which is only ever decided per row by Rust. Clearing the whole
 * mirror on any settle would un-hide every other session the clinician deleted.
 */
export const withoutServerSessionId = (prev: string[], id: string): string[] => {
  const next = prev.filter((existing) => existing !== id);
  return next.length === prev.length ? prev : next;
};

/**
 * Fold a durable list of still-owed ids into the mirror.
 *
 * `snapshot` is what the mirror held when the read STARTED, and it is what makes
 * this safe to apply out of order. An id in the snapshot that the backend no
 * longer owes is dropped — that is the reconciliation, and it is how a delete
 * that replayed, gave up, or was abandoned stops hiding its session without any
 * status rule being duplicated on this side. An id that was NOT in the snapshot
 * is kept whatever the read says, because it was queued after the read was
 * taken and the read cannot have seen it. Two windows, or an enqueue landing
 * mid-read, therefore converge on the same set whichever order they resolve in.
 */
export const reconcilePendingServerDeleteIds = (
  prev: string[],
  snapshot: readonly string[],
  owed: readonly string[]
): string[] => {
  const owedIds = new Set(owed);
  const known = new Set(snapshot);
  const held = new Set(prev);
  // Ids the mirror held when the read STARTED and does not hold now. Only a
  // settled event removes an id, so each of these is Rust saying the delete
  // stopped being owed — and saying it AFTER this reply was computed. The
  // reply is stale about them, so it may not put them back. Without this the
  // mirror resurrects an id nothing will settle a second time, hiding a
  // session the clinician has just been told came back.
  const settledDuringRead = new Set(snapshot.filter((id) => !held.has(id)));
  const kept = prev.filter((id) => owedIds.has(id) || !known.has(id));
  const next = unionServerSessionIds(
    kept,
    owed.filter((id) => !settledDuringRead.has(id))
  );
  return next.length === prev.length && next.every((id, i) => id === prev[i])
    ? prev
    : next;
};

/**
 * Local session ids the user has manually deleted in this renderer session.
 * Used to suppress late engine events that race with manual deletion before the
 * Rust `offline_recording:deleted` event reaches the WebView.
 */
export const offlineDeletedLocalSessionIdsAtom = atom<string[]>([]);

/**
 * Session ids whose "Synced" tick has had its moment and should no longer show
 * one. Holds both the local and the server id for a recording, because a list
 * row is keyed locally until its server session exists and by the server id
 * afterwards, and the tick has to stay dismissed across that handover.
 *
 * Deliberately separate from the queue row rather than a field on it, so that
 * hiding the tick and removing the row stay independent decisions — see
 * `scheduleSyncedDismissal` in `useOfflineRecordingEvents` for why the row has
 * to stay.
 *
 * In-memory and per-run, like the deleted-ids atom above: the tick is an
 * acknowledgement of something that just happened, so there is nothing to
 * remember across a relaunch. Bounded by the recordings synced in one app run.
 */
export const offlineDismissedSyncedBadgeIdsAtom = atom<string[]>([]);

// --- Derived (read-only) atoms ---------------------------------------------

/** Total recordings tracked in the queue. */
export const offlineQueueCountAtom = atom((get) => get(offlineSyncQueueAtom).length);

/** Recordings waiting to sync or mid-sync (not done, not halted). */
export const offlinePendingCountAtom = atom(
  (get) =>
    get(offlineSyncQueueAtom).filter((item) => isOfflinePendingStatus(item.status)).length
);

/** Recordings that halted and need attention/retry (incl. `auth_required`). */
export const offlineFailedCountAtom = atom(
  (get) =>
    get(offlineSyncQueueAtom).filter((item) => isOfflineFailedStatus(item.status)).length
);

/**
 * True while an offline capture is actively recording — a live capture that is
 * NOT paused. Mirrors online's "activeSessionIdAtom set only while recording"
 * semantics (paused is navigable), so the cross-session navigation guard can
 * treat a running offline capture exactly like a running online one.
 */
export const offlineCaptureRunningAtom = atom(
  (get) => get(offlineActiveRecordingIdAtom) !== null && !get(offlineCapturePausedAtom)
);

/** The currently-recording item resolved from the queue, or `null`. */
export const offlineActiveRecordingAtom = atom((get) => {
  const id = get(offlineActiveRecordingIdAtom);
  if (id === null) {
    return null;
  }
  return get(offlineSyncQueueAtom).find((item) => item.localSessionId === id) ?? null;
});

/**
 * True when the residency-pinned backend is reachable and ready to drain the
 * queue — the FE mirror of the Rust `online_ready_to_sync` trigger.
 */
export const offlineReadyToSyncAtom = atom(
  (get) => get(offlineConnectivityStateAtom) === 'online_ready_to_sync'
);

/**
 * True when the residency-pinned backend is NOT reachable — the recorder /
 * new-session flow should route into offline capture (APP-6666, US2; replaces
 * the interim `window.__heidiOfflineTest` dev toggle). `unknown` (pre-first
 * read) and `online_ready_to_sync` both read as reachable, so we never flash a
 * false "you're offline" before the first connectivity reading.
 */
export const offlineBackendUnreachableAtom = atom((get) => {
  const state = get(offlineConnectivityStateAtom);
  return state === 'offline' || state === 'backend_unreachable';
});
